import {
  DEFAULT_BOARD_BUILDER_FILTERS,
  DEFAULT_CORRELATIONS_FILTERS,
  DEFAULT_PLAYER_PACKS_FILTERS,
  DEFAULT_SCORING_FILTERS,
  DEFAULT_SIMULATIONS_FILTERS,
  DEFAULT_TRADING_FILTERS
} from '@bo-constants/filters';
import {
  useLeagueDurationAttributes,
  useLeagueDurationsAttributes
} from '@bo-hooks/useLeagueDurationAttributes';
import type { Dashboards } from '@bo-types/common';
import type { DateTime } from 'luxon';
import type { DefaultFilters } from '@library/types/filters';
import { rankThenNameSort } from '@bo-utils/leagueProjectionFilters';
import { useGlobalFilters } from '@bo-hooks/filters/useGlobalFilters';
import { useLeagueDependentFilters } from '@bo-hooks/filters/useLeagueDependentFilters';
import { useLeagues } from '@bo-hooks/useLeagues';

export const useDashBoard = ({
  dashboardName,
  initialStartDate = undefined,
  initialEndDate = undefined
}: {
  dashboardName: Dashboards;
  initialStartDate?: DateTime;
  initialEndDate?: DateTime;
}) => {
  const {
    bulkLeagueIdGroups,
    handleSelectLeague,
    isLeagueError,
    isLeagueLoading,
    leagueInfo,
    leaguesList,
    selectedLeagueId
  } = useLeagues();

  const {
    durations,
    fromDate,
    handleMultiSelectDurations,
    handleSearch,
    handleSelectDateRange,
    searchValue,
    selectedDuration,
    selectedDurations,
    selectedEndDate,
    selectedStartDate,
    toDate
  } = useGlobalFilters({ initialStartDate, initialEndDate, leagueInfo });

  let defaultFilters: DefaultFilters;

  switch (dashboardName) {
    case 'BOARD_BUILDER':
      defaultFilters = DEFAULT_BOARD_BUILDER_FILTERS;
      break;
    case 'CORRELATIONS':
      defaultFilters = DEFAULT_CORRELATIONS_FILTERS;
      break;
    case 'SIMULATIONS':
      defaultFilters = DEFAULT_SIMULATIONS_FILTERS;
      break;
    case 'SCORING':
      defaultFilters = DEFAULT_SCORING_FILTERS;
      break;
    case 'TRADING':
      defaultFilters = DEFAULT_TRADING_FILTERS;
      break;
    case 'STREAK':
      defaultFilters = DEFAULT_BOARD_BUILDER_FILTERS;
      break;
    case 'POLLS':
      defaultFilters = DEFAULT_BOARD_BUILDER_FILTERS;
      break;
    case 'PLAYER_PACKS':
      defaultFilters = DEFAULT_PLAYER_PACKS_FILTERS;
      break;
    default:
      defaultFilters = DEFAULT_TRADING_FILTERS;
  }

  const { changeFilterValue, changeFilterValues, filters, setFilters } =
    useLeagueDependentFilters<DefaultFilters>({
      defaultFilters
    });

  const selectedLeagueDurationAttributes = useLeagueDurationAttributes({
    leagueInfo,
    durationId: selectedDuration
  });

  const selectedLeaguesDurationAttributes = useLeagueDurationsAttributes({
    leagueInfo,
    durationIds: selectedDurations
  });

  return {
    bulkLeagueIdGroups,
    changeFilterValue,
    changeFilterValues,
    durations: durations
      ? rankThenNameSort({ leagueProjectionAttributesList: durations })
      : [],
    filters,
    fromDate,
    handleMultiSelectDurations,
    handleSearch,
    handleSelectDateRange,
    handleSelectLeague,
    isLeagueError,
    isLeagueLoading,
    leagueInfo,
    leaguesList,
    searchValue,
    selectedDuration,
    selectedDurations,
    selectedEndDate,
    selectedLeagueDurationAttributes,
    selectedLeaguesDurationAttributes,
    selectedLeagueId,
    selectedStartDate,
    setFilters,
    toDate
  };
};
